import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import BookingStatusPill from '../BookingDetail/BookingStatusPill';
import styles from './ScheduleCard.module.css';

export interface ScheduleCardProps {
    service: string;
    customer: string;
    contact: string;
    duration: string;
    price: string;
    status: string;
    image: string;
    isShrinked: boolean;
    appointmentId?: string;
    /** Owner: Edit + Delete menu */
    onEdit?: (appointmentId: string) => void;
    onDelete?: (appointmentId: string) => void | Promise<void>;
    /** Owner: View Payment details */
    onViewPayment?: (appointmentId: string) => void;
    /** Staff: View Details + Reschedule menu */
    onViewDetails?: (appointmentId: string) => void;
    onReschedule?: (appointmentId: string) => void;
}

const ScheduleCard: React.FC<ScheduleCardProps> = ({
    service, customer, contact, duration, price, status, image, isShrinked,
    appointmentId, onEdit, onDelete, onViewPayment, onViewDetails, onReschedule
}) => {
    const { t } = useLanguage();
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLButtonElement>(null);

    const hasOwnerMenu = onEdit != null || onDelete != null || onViewPayment != null;
    const hasStaffMenu = onViewDetails != null || onReschedule != null;
    const showMenu = hasOwnerMenu || hasStaffMenu;

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 5,
                left: rect.right - 200
            });
        }
        setDropdownVisible(!isDropdownVisible);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
            menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setDropdownVisible(false);
            setShowDeleteConfirm(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (isDropdownVisible || showDeleteConfirm) {
                setDropdownVisible(false);
                setShowDeleteConfirm(false);
            }
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [isDropdownVisible, showDeleteConfirm]);

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (appointmentId && onEdit) onEdit(appointmentId);
        setDropdownVisible(false);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setShowDeleteConfirm(true);
        setDropdownVisible(false);
    };

    const handleConfirmDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!appointmentId || !onDelete) return;
        setIsDeleting(true);
        try {
            await onDelete(appointmentId);
            setShowDeleteConfirm(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setShowDeleteConfirm(false);
    };

    const handleViewPayment = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (appointmentId && onViewPayment) onViewPayment(appointmentId);
        setDropdownVisible(false);
    };

    const handleViewDetails = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (appointmentId && onViewDetails) onViewDetails(appointmentId);
        setDropdownVisible(false);
    };

    const handleReschedule = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (appointmentId && onReschedule) onReschedule(appointmentId);
        setDropdownVisible(false);
    };

    const statusClass = status.toLowerCase();

    const dropdownContent = (isDropdownVisible || showDeleteConfirm) && createPortal(
        <div
            className={styles.dropdownMenu}
            ref={dropdownRef}
            style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: '200px',
                zIndex: 10000
            }}
        >
            {isDropdownVisible && !showDeleteConfirm && hasOwnerMenu && (
                <>
                    {onEdit && (
                        <button type="button" className={styles.dropdownItem} onClick={handleEdit}>
                            <svg className={styles.icon} width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path style={{ stroke: 'var(--text-primary)' }} d="M6.43624 8.24985C6.32883 8.24985 6.27513 8.19614 6.22143 8.14244C6.16773 8.08874 6.16773 7.98133 6.22143 7.76652L6.86586 6.15545C6.97327 5.88693 7.18808 5.56472 7.40289 5.34991L11.5917 1.16111C11.6991 1.0537 11.8065 1 11.9676 1C12.1287 1 12.2898 1.0537 12.3972 1.16111L13.2028 1.96665C13.3102 2.07405 13.3639 2.23516 13.3639 2.39627C13.3639 2.55737 13.3102 2.71848 13.2028 2.82589L9.01396 7.01469C8.79915 7.2295 8.47694 7.44431 8.20842 7.55171L6.59735 8.24985C6.59735 8.24985 6.48994 8.24985 6.43624 8.24985Z" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                <path style={{ stroke: 'var(--text-primary)' }} d="M11.3767 6.96098V14.1034C11.3767 14.6942 10.8934 15.1775 10.3027 15.1775H1.71028C1.11955 15.1775 0.63623 14.6942 0.63623 14.1034V2.82588C0.63623 2.23515 1.11955 1.75183 1.71028 1.75183H7.99348" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span>{t('ost.card.edit')}</span>
                        </button>
                    )}
                    {onViewPayment && (
                        <>
                            {onEdit && <div className={styles.separator}></div>}
                            <button type="button" className={styles.dropdownItem} onClick={handleViewPayment}>
                                <svg className={styles.icon} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect style={{ stroke: 'var(--text-primary)' }} x="1" y="3" width="14" height="10" rx="2" strokeWidth="1.2" />
                                    <path style={{ stroke: 'var(--text-primary)' }} d="M1 6.5h14" strokeWidth="1.2" />
                                    <path style={{ stroke: 'var(--text-primary)' }} d="M4 9.5h3" strokeWidth="1.2" strokeLinecap="round" />
                                </svg>
                                <span>{t('ost.card.viewPayment')}</span>
                            </button>
                        </>
                    )}
                    {(onEdit || onViewPayment) && onDelete && <div className={styles.separator}></div>}
                    {onDelete && (
                        <button type="button" className={styles.dropdownItem} onClick={handleDeleteClick}>
                            <svg className={styles.icon} width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path style={{ stroke: 'var(--destructive-fg)' }} d="M14.4762 4.31384L13.5925 17.2745C13.5925 18.0109 13.0034 18.6 12.267 18.6H4.97663C4.24023 18.6 3.65111 18.0109 3.65111 17.2745L2.84106 4.31384" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                <path style={{ stroke: 'var(--destructive-fg)' }} d="M5.19751 4.24017L5.27115 2.1046C5.27115 1.51548 5.71299 1 6.15483 1H11.0887C11.6042 1 11.9724 1.51548 11.9724 2.1046L12.046 4.24017" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                <path style={{ stroke: 'var(--destructive-fg)' }} d="M1 4.24023H16.3172" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                <path style={{ stroke: 'var(--destructive-fg)' }} d="M5.71289 7.18579L6.22837 16.1699" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                <path style={{ stroke: 'var(--destructive-fg)' }} d="M8.21655 10.2051L8.51111 16.1699" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span>{t('ost.card.delete')}</span>
                        </button>
                    )}
                </>
            )}
            {isDropdownVisible && !showDeleteConfirm && hasStaffMenu && (
                <>
                    {onViewDetails && (
                        <button type="button" className={styles.dropdownItem} onClick={handleViewDetails}>
                            <svg className={styles.icon} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path style={{ stroke: 'var(--text-primary)' }} d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <circle style={{ stroke: 'var(--text-primary)' }} cx="8" cy="8" r="2" strokeWidth="1.5" />
                            </svg>
                            <span>{t('ost.card.viewDetails')}</span>
                        </button>
                    )}
                    {onReschedule && (
                        <button type="button" className={styles.dropdownItem} onClick={handleReschedule}>
                            <svg className={styles.icon} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path style={{ stroke: 'var(--text-primary)' }} d="M8 1.333c3.681 0 6.667 2.986 6.667 6.667 0 3.681-2.986 6.667-6.667 6.667-3.681 0-6.667-2.986-6.667-6.667" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path style={{ stroke: 'var(--text-primary)' }} d="M8 4v4l2.667 1.333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path style={{ stroke: 'var(--text-primary)' }} d="M1.333 8H4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span>{t('ost.card.reschedule')}</span>
                        </button>
                    )}
                </>
            )}
            {showDeleteConfirm && (
                <div className={styles.confirmDelete}>
                    <p>{t('ost.card.deleteConfirm')}</p>
                    <div className={styles.confirmButtons}>
                        <button type="button" onClick={handleCancelDelete} className={styles.cancelBtn}>{t('ost.card.cancel')}</button>
                        <button
                            type="button"
                            onClick={handleConfirmDelete}
                            className={styles.deleteBtn}
                            disabled={isDeleting}
                        >
                            {isDeleting ? t('ost.card.deleting') : t('ost.card.delete')}
                        </button>
                    </div>
                </div>
            )}
        </div>,
        document.body
    );

    return (
        <div className={`${styles.scheduleCard} ${styles[statusClass]} ${isShrinked ? styles.isShrinked : ''}`}>
            {showMenu && (
                <button
                    type="button"
                    className={`${styles.menu} menu`}
                    onClick={handleMenuClick}
                    ref={menuRef}
                    aria-label={t('ost.card.menu')}
                    aria-haspopup="menu"
                    aria-expanded={isDropdownVisible}
                >
                    <svg className={styles.menuIcon} viewBox="0 0 6 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path style={{ fill: 'var(--text-tertiary)' }} d="M2.54902 5.1C1.07843 5.1 0 4 0 2.6C0 1.1 1.07843 0 2.54902 0C3.92157 0 5 1.1 5 2.6C5 4 3.92157 5.1 2.54902 5.1Z" />
                        <path style={{ fill: 'var(--text-tertiary)' }} d="M2.54902 14.6C1.07843 14.6 0 13.4 0 12C0 10.6 1.07843 9.40002 2.54902 9.40002C3.92157 9.40002 5.09804 10.5 5.09804 12C5 13.4 3.92157 14.6 2.54902 14.6Z" />
                        <path style={{ fill: 'var(--text-tertiary)' }} d="M2.54902 24C1.07843 24 0 22.9 0 21.4C0 20 1.07843 18.8 2.54902 18.8C3.92157 18.8 5.09804 19.9 5.09804 21.4C5 22.9 3.92157 24 2.54902 24Z" />
                    </svg>
                </button>
            )}
            {dropdownContent}
            <div className={styles.scheduleDetails}>
                <div className={styles.service}>{service}</div>
                <div className={styles.customerDetails}>
                    <img src={image} alt={customer} className={styles.customerImage} />
                    <div className={styles.customerNamePhone}>
                        <div className={styles.customerName}>{customer}</div>
                        <div className={styles.contact}>{contact}</div>
                    </div>
                </div>
                <div className={styles.durationPriceStatus}>
                    <div className={styles.durationPrice}>
                        <div className={styles.duration}>{duration}</div>
                        <div className={styles.price}>{price}</div>
                    </div>
                    {/* O3 (SO-41/SO-42) — shared owner status words + colours. */}
                    <BookingStatusPill status={status} />
                </div>
            </div>
        </div>
    );
};

export default ScheduleCard;
